/**
 * Signup flow state passing.
 *
 * Text fields travel through URL search params (password-in-URL is an
 * explicitly accepted tradeoff for this flow) and every hop — forward and BACK
 * alike — carries the whole query forward, so re-entering a step always finds
 * its data in the URL. Profile / student-card / HKID images are data URLs and
 * far too large for a URL, so they are staged in a module-level memory store
 * and mirrored into sessionStorage when it has room (cleared on success) —
 * only a short marker travels in the URL. The last step's dropdown selections
 * (university / gender / identity / year of study) have no URL representation
 * at all, so they live in the same kind of memory store.
 *
 * The memory copy is authoritative: sessionStorage is capped (~5MB in Safari,
 * ~10MB in Chrome) and `setItem` throws QuotaExceededError once full, which
 * used to lose the last picked image without any visible error. Signup runs in
 * a single tab, so the storage write is only a reload convenience.
 */

export interface SignupParams {
  code?: string;
  username?: string;
  email?: string;
  phoneNum?: string;
  password?: string;
  profile?: string;
  studentCard?: string;
  hkid?: string;
}

type SignupImageSlot = "profile" | "studentCard" | "hkid";

const PROFILE_KEY = "uninfo_signup_profile";
const STUDENT_CARD_KEY = "uninfo_signup_student_card";
const HKID_KEY = "uninfo_signup_hkid";

const IMAGE_KEYS: Record<SignupImageSlot, string> = {
  profile: PROFILE_KEY,
  studentCard: STUDENT_CARD_KEY,
  hkid: HKID_KEY,
};

const memoryImages: Partial<Record<SignupImageSlot, string>> = {};

/** Chrome/Edge: QuotaExceededError. Firefox: NS_ERROR_DOM_QUOTA_REACHED (1014). */
const isQuotaExceeded = (err: unknown): boolean => {
  const { name, code } = (err || {}) as { name?: string; code?: number };
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    code === 22 ||
    code === 1014
  );
};

/**
 * Stores an image for the rest of the signup flow.
 * @returns true when it also fits in sessionStorage (survives a page reload).
 */
const setSignupImage = (slot: SignupImageSlot, dataUrl: string): boolean => {
  memoryImages[slot] = dataUrl;
  const key = IMAGE_KEYS[slot];
  try {
    sessionStorage.setItem(key, dataUrl);
    return true;
  } catch (err) {
    // Drop the previous value so a reload can never send a stale image.
    try {
      sessionStorage.removeItem(key);
    } catch {
      // nothing else to do — the memory copy is what this tab will submit
    }
    if (!dataUrl) return true; // clearing an image never blocks the flow
    if (isQuotaExceeded(err)) {
      console.error(
        `[signupState] sessionStorage quota exceeded while saving ${slot} (${dataUrl.length} chars), keeping it in memory for this tab: ${err}`,
      );
    } else {
      console.error(
        `[signupState] could not save ${slot} to sessionStorage, keeping it in memory for this tab: ${err}`,
      );
    }
    return false;
  }
};

const getSignupImage = (slot: SignupImageSlot): string => {
  const inMemory = memoryImages[slot];
  if (inMemory !== undefined) return inMemory;
  try {
    return sessionStorage.getItem(IMAGE_KEYS[slot]) || "";
  } catch {
    return "";
  }
};

export const buildSignupQuery = (params: SignupParams): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const readSignupParams = (search: string): SignupParams => {
  const params = new URLSearchParams(search);
  const out: SignupParams = {};
  [
    "code",
    "username",
    "email",
    "phoneNum",
    "password",
    "profile",
    "studentCard",
    "hkid",
  ].forEach((key) => {
    const value = params.get(key);
    if (value) out[key as keyof SignupParams] = value;
  });
  return out;
};

export const setSignupProfile = (dataUrl: string) => setSignupImage("profile", dataUrl);

export const getSignupProfile = (): string => getSignupImage("profile");

export const setSignupStudentCard = (dataUrl: string) =>
  setSignupImage("studentCard", dataUrl);

export const getSignupStudentCard = (): string => getSignupImage("studentCard");

export const setSignupHKID = (dataUrl: string) => setSignupImage("hkid", dataUrl);

export const getSignupHKID = (): string => getSignupImage("hkid");

export const clearSignupImages = () => {
  (Object.keys(IMAGE_KEYS) as SignupImageSlot[]).forEach((slot) => {
    delete memoryImages[slot];
    try {
      sessionStorage.removeItem(IMAGE_KEYS[slot]);
    } catch {
      // nothing else to do — the memory copy is what this tab submits
    }
  });
};

/**
 * Last-step selections. They are short, so unlike the images they comfortably
 * fit in sessionStorage, but they use the same memory-first pattern: a failed
 * write must never cost the user their dropdown choices.
 */
export type SignupFieldKey = "university" | "gender" | "identity" | "yearOfStudy";

const FIELDS_KEY = "uninfo_signup_fields";

const memoryFields: Partial<Record<SignupFieldKey, string>> = {};

const readStoredFields = (): Partial<Record<SignupFieldKey, string>> => {
  try {
    const raw = sessionStorage.getItem(FIELDS_KEY);
    return raw ? (JSON.parse(raw) as Partial<Record<SignupFieldKey, string>>) : {};
  } catch {
    return {};
  }
};

export const setSignupField = (key: SignupFieldKey, value: string) => {
  memoryFields[key] = value;
  try {
    sessionStorage.setItem(FIELDS_KEY, JSON.stringify({ ...readStoredFields(), [key]: value }));
  } catch (err) {
    console.error(
      `[signupState] could not save ${key} to sessionStorage, keeping it in memory for this tab: ${err}`,
    );
  }
};

export const getSignupField = (key: SignupFieldKey): string => {
  const inMemory = memoryFields[key];
  if (inMemory !== undefined) return inMemory;
  return readStoredFields()[key] || "";
};

export const clearSignupFields = () => {
  (Object.keys(memoryFields) as SignupFieldKey[]).forEach((key) => {
    delete memoryFields[key];
  });
  try {
    sessionStorage.removeItem(FIELDS_KEY);
  } catch {
    // nothing else to do — the memory copy is what this tab reads
  }
};